import { Chip, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  formatValidityBound,
  membershipState,
  membershipStateColor,
  type MembershipValidity,
} from './orgUnitMembership';

/**
 * The membership's effective state, shared by the org unit's Members tab and the per-user
 * memberships card so the two can never disagree. Green means "grants access right now" and nothing
 * else — see orgUnitMembership.ts for why `isActive` alone was not enough.
 */
export function MembershipStateChip({ member }: { member: MembershipValidity }) {
  const { t } = useTranslation();
  const state = membershipState(member);

  const label = {
    ACTIVE: t('common.active'),
    INACTIVE: t('common.inactive'),
    EXPIRED: t('orgUnit.membershipExpired', { defaultValue: 'Expired' }),
    NOT_YET_VALID: t('orgUnit.membershipNotYetValid', { defaultValue: 'Not yet valid' }),
    INVALID_RANGE: t('orgUnit.membershipInvalidRange', { defaultValue: 'Invalid dates' }),
  }[state];

  const chip = (
    <Chip label={label} size="small" color={membershipStateColor(state)} />
  );

  if (state === 'ACTIVE' || state === 'INACTIVE') return chip;

  // The window is the whole explanation, so it travels with the chip rather than making the reader
  // correlate two columns.
  const reason = state === 'INVALID_RANGE'
    ? t('orgUnit.membershipInvalidRangeHint', {
      defaultValue: 'Valid to is before Valid from, so this membership can never grant access.',
    })
    : t('orgUnit.membershipWindowHint', {
      defaultValue: 'Grants access only between {{from}} and {{to}}.',
      from: formatValidityBound(member.validFrom),
      to: formatValidityBound(member.validTo),
    });

  return <Tooltip title={reason}><span>{chip}</span></Tooltip>;
}
